import {
  Calendar,
  Users,
  Briefcase,
  LayoutDashboard,
  Settings,
  Mail,
  Zap,
  FileInput,
  MapPin,
  Clock,
  MessageSquare,
  FileText,
  UserCog,
  ClipboardList,
  FileSpreadsheet,
  MailCheck,
  Briefcase as JobIcon,
  Palmtree,
  Bell,
  DollarSign,
  History,
  Download,
  LogOut,
  Package,
  MessagesSquare,
  Building2,
  CreditCard,
  ChevronDown,
  CheckSquare,
  Folder,
  UserCircle,
  Activity,
  Scale,
  Inbox as InboxIcon,
  TrendingUp,
  Shield,
  DollarSign as DollarSignIcon,
  CreditCard as CreditCardIcon,
  Users as UsersIcon,
  Activity as ActivityIcon,
  Mail as MailIcon,
  MessageSquare as MessageSquareIcon,
  FileText as FileTextIcon,
  Megaphone,
  HelpCircle,
  Rocket,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuthStore } from "@/stores/authStore";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboardingTour } from "@/contexts/OnboardingTourContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Section 1: WORK - Day-to-day job operations
const workItems = [
  { title: "Dashboard", url: "/", icon: LayoutDashboard },
  { title: "Tracking", url: "/tracking", icon: TrendingUp },
  { title: "Calendar", url: "/calendar", icon: Calendar },
  { title: "Job Scheduler", url: "/scheduler", icon: Zap },
  { title: "Map", url: "/map", icon: MapPin },
  { title: "Jobs", url: "/jobs", icon: Briefcase },
  { title: "Quotes", url: "/quotes", icon: FileSpreadsheet },
  { title: "Invoices", url: "/invoices", icon: DollarSign },
  { title: "Form Submissions", url: "/form-submissions", icon: FileInput },
];

// Section 2: CUSTOMERS - Client and lead management
const customerItems = [
  { title: "Customers", url: "/customers", icon: Users },
  { title: "Inbox", url: "/inbox", icon: InboxIcon },
  { title: "Messages", url: "/messages", icon: Mail },
];

// Section 3: STAFF - Team & workforce management
const staffManagementItems = [
  { title: "Team", url: "/team", icon: Users, adminOnly: true },
  { title: "Staff Availability", url: "/staff-availability", icon: Palmtree, adminOnly: true },
  { title: "Staff Schedule", url: "/staff-schedule", icon: UserCog },
  { title: "Timesheets", url: "/timesheets", icon: Clock },
];

// Section 4: OPERATIONS - Templates, products & integrations
const operationsItems = [
  // Hidden temporarily - Form Builder kept for future use
  // { title: "Form Library", url: "/forms", icon: ClipboardList, adminOnly: true },
  { title: "Job Templates", url: "/job-templates", icon: FileText, adminOnly: true },
  { title: "Quote Templates", url: "/quote-templates", icon: FileSpreadsheet, adminOnly: true },
  { title: "Email/SMS Templates", url: "/email-sms-templates", icon: MailCheck, adminOnly: true },
  { title: "Automation Settings", url: "/automation-settings", icon: Zap, adminOnly: true },
  { title: "Products / Items", url: "/items", icon: Package },
  { title: "Integrations", url: "/integrations", icon: Zap, adminOnly: true },
];

// Section 4: SYSTEM - Settings, billing, and account
const systemItems = [
  { title: "Settings", url: "/settings", icon: Settings, adminOnly: true },
  { title: "Business Profile", url: "/settings/business", icon: Building2, adminOnly: true },
  { title: "Billing & Subscription", url: "/billing-settings", icon: CreditCard, adminOnly: true },
  { title: "Legal", url: "/settings/legal", icon: Scale, adminOnly: true },
  { title: "Data Export", url: "/data-export", icon: Download, adminOnly: true },
  { title: "TradeReady Feedback", url: "/workdeck-feedback", icon: MessagesSquare, adminOnly: true },
];

// Staff Portal Items
const staffItems = [
  { title: "My Jobs", url: "/staff-dashboard", icon: JobIcon },
  { title: "My Availability", url: "/staff-availability", icon: Palmtree },
  { title: "Map View", url: "/map", icon: MapPin },
  { title: "Team Chat", url: "/staff-portal/team-chat", icon: MessageSquare },
  // { title: "Notifications", url: "/notification-settings", icon: Bell },
  { title: "Settings", url: "/staff-settings", icon: Settings },
];

export function AppSidebar() {
  const [location] = useLocation();
  const { user, isAdmin, isSuperAdmin, isLoading } = useAuth();
  const { highlightedUrls, isVisible: tourVisible, reopen, wasDismissed, isReopening, currentStep } = useOnboardingTour();
  const { isMobile, setOpenMobile } = useSidebar();
  const queryClient = useQueryClient();
  // State for collapsible sections (default: only Work expanded)
  const [workOpen, setWorkOpen] = useState(true);
  const [customersOpen, setCustomersOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  const [operationsOpen, setOperationsOpen] = useState(false);
  const [systemOpen, setSystemOpen] = useState(false);
  const [staffPortalOpen, setStaffPortalOpen] = useState(true);

  /** Returns true when the tour is active and this URL is highlighted for the current step */
  const isTourHighlighted = (url: string) =>
    tourVisible && highlightedUrls.some((h) => h === url);

  /** Returns true when this item is the PRIMARY item for the current tour step (used for positioning the popup) */
  const isTourPrimary = (url: string) =>
    tourVisible && currentStep && currentStep.primaryUrl === url;

  /** Returns true when tour is active but this item is NOT the highlighted one */
  const isTourDimmed = (url: string) =>
    tourVisible && !highlightedUrls.some((h) => h === url);

  /** Returns true when tour is active and this section is NOT the one containing the highlighted item */
  const isSectionDimmed = (section: string) =>
    tourVisible && currentStep && currentStep.section !== section;

  /** During tour, prevent toggling sections manually */
  const sectionOnChange = (section: string, setter: (v: boolean) => void) => {
    if (tourVisible) return; // Don't allow toggling during tour
    return (v: boolean) => setter(v);
  };

  // Auto-expand the section for the current tour step and collapse others
  useEffect(() => {
    if (!tourVisible || !currentStep) return;
    const section = currentStep.section;
    // Open only the relevant section, close the rest
    setWorkOpen(section === "WORK");
    setCustomersOpen(section === "CUSTOMERS");
    setStaffOpen(section === "STAFF");
    setOperationsOpen(section === "OPERATIONS");
    setSystemOpen(section === "SYSTEM");
    setStaffPortalOpen(section === "MY WORKSPACE" || !tourVisible);
  }, [tourVisible, currentStep]);

  // Helper function to check if a route is active
  // Handles exact matches and routes with query parameters
  const isRouteActive = (url: string) => {
    // Remove query parameters and hash from location for comparison
    const currentPath = location.split('?')[0].split('#')[0];
    const targetPath = url.split('?')[0].split('#')[0];
    
    // Exact match
    if (currentPath === targetPath) return true;
    
    // Special case for root path - only match exactly
    if (targetPath === '/') return false;

    // Special case: Prevent the parent "/settings" tab from highlighting 
    // when visiting child routes that have their own sidebar menu items.
    if (targetPath === '/settings' && (currentPath === '/settings/business' || currentPath === '/settings/legal')) {
      return false;
    }
    
    // For non-root paths, check if current path starts with target path
    // This handles nested routes (e.g., /jobs/123 should match /jobs)
    if (currentPath.startsWith(targetPath + '/')) return true;
    
    return false;
  };

  const closeMobileDrawer = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  // Fetch Team Chat unread count
  const { data: teamChatUnreadData } = useQuery<{ count: number }>({
    queryKey: ["/team-chat/unread-count"],
    refetchInterval: false, // Don't poll - only fetch once
    enabled: !!user && !isLoading,
  });

  // Fetch Inbox unread count
  const { data: inboxUnreadData } = useQuery<{ count: number }>({
    queryKey: ["/inbox/unread-count"],
    refetchInterval: false, // Don't poll - only fetch once
    enabled: !!user && !isLoading,
  });
  
  const inboxUnreadCount = inboxUnreadData?.count || 0;
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const handleLogout = () => {
    setShowLogoutDialog(true);
  };

  const clearAuth = useAuthStore((state) => state.clearAuth);

  const confirmLogout = async () => {
    try {
      // Call backend logout endpoint to clear refresh token cookie
      // Note: We import dynamically to avoid circular dependencies
      const { logout } = await import("@/features/auth/api/authApi");
      try {
        await logout();
      } catch (logoutError) {
        // Log error but continue with client-side cleanup
        console.error("Backend logout error (continuing with client cleanup):", logoutError);
      }
      
      // Cancel any in-flight queries to prevent updates during unmount
      try {
        queryClient.cancelQueries({ queryKey: ["/auth/user"] });
        queryClient.setQueryData(["/auth/user"], null);
        queryClient.removeQueries({ queryKey: ["/auth/user"] });
      } catch (queryError) {
        // Ignore query errors during logout
        console.debug("Query cleanup error (safe to ignore):", queryError);
      }
      
      // Clear auth state from Zustand store (access token in memory)
      try {
        clearAuth();
      } catch (authError) {
        // Ignore auth store errors during logout
        console.debug("Auth store cleanup error (safe to ignore):", authError);
      }
    } catch (error) {
      console.error("Logout error:", error);
      // Even if there's an error, still redirect to login
    }
    
    // Immediately redirect - this stops all React execution
    // Use replace to prevent back button from going to dashboard
    // This causes a full page reload which will clear all React state cleanly
    try {
      window.location.replace("/login");
    } catch (redirectError) {
      // Fallback if replace fails
      console.error("Redirect error:", redirectError);
      window.location.href = "/login";
    }
  };

  return (
    <Sidebar className={tourVisible ? "!z-[45]" : undefined}>
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-white dark:bg-gray-800 flex items-center justify-center p-1">
            <img src="/images/logo.png" alt="TradeReady" className="h-full w-full object-contain" />
          </div>
          <div className="flex flex-col">
            <span className="font-semibold text-base leading-tight">TradeReady</span>
            <span className="text-xs text-muted-foreground leading-tight">All Matters Group</span>
          </div>
        </div>
        {user && (
          <div className="mt-3 p-2 rounded-md bg-muted/50 space-y-1">
            <div className="text-xs font-medium truncate">
              {(() => {
                // Get display name with proper fallbacks
                const firstName = user.firstName?.trim() || "";
                const lastName = user.lastName?.trim() || "";
                const fullName = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || "";
                const username = user.username?.trim() || "";
                const email = user.email?.trim() || "";
                
                // Return the best available option
                return fullName || username || email || "User";
              })()}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {user.role === "owner" ? "Owner" : user.role === "admin" ? "Administrator" : user.role === "staff" ? "Staff Member" : "Super Admin"}
            </div>
          </div>
        )}
      </SidebarHeader>
      <SidebarContent>
        {/* Show loading state while auth is loading to prevent flash */}
        {isLoading ? (
          <div className="p-4 space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-8 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : isSuperAdmin ? (
          <>
            {/* Super Admin Section */}
            <SidebarGroup>
              <SidebarGroupLabel>SUPER ADMIN</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isRouteActive("/super-admin/dashboard")}>
                      <Link href="/super-admin/dashboard">
                        <LayoutDashboard className="h-4 w-4" />
                        <span>Dashboard</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isRouteActive("/super-admin/tenants")}>
                      <Link href="/super-admin/tenants">
                        <Shield className="h-4 w-4" />
                        <span>All Users</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {/* Revenue & Financial */}
            <SidebarGroup>
              <SidebarGroupLabel>REVENUE & FINANCIAL</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isRouteActive("/super-admin/revenue/dashboard")}>
                      <Link href="/super-admin/revenue/dashboard">
                        <DollarSignIcon className="h-4 w-4" />
                        <span>Revenue Dashboard</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isRouteActive("/super-admin/payments/analytics")}>
                      <Link href="/super-admin/payments/analytics">
                        <CreditCardIcon className="h-4 w-4" />
                        <span>Payment Analytics</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {/* Support & Communication */}
            <SidebarGroup>
              <SidebarGroupLabel>SUPPORT & COMMUNICATION</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isRouteActive("/super-admin/emails")}>
                      <Link href="/super-admin/emails">
                        <MailIcon className="h-4 w-4" />
                        <span>Email Logs</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isRouteActive("/super-admin/sms")}>
                      <Link href="/super-admin/sms">
                        <MessageSquareIcon className="h-4 w-4" />
                        <span>SMS Logs</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        ) : isAdmin ? (
          <>
            {/* Section 1: WORK */}
            <Collapsible open={workOpen} onOpenChange={sectionOnChange("WORK", setWorkOpen)} className="group/collapsible">
              <SidebarGroup>
                <SidebarGroupLabel asChild>
                  <CollapsibleTrigger className={cn("flex w-full items-center justify-between hover-elevate transition-opacity duration-200", isSectionDimmed("WORK") && "opacity-30 pointer-events-none")}>
                    <span className="font-semibold text-sm">WORK</span>
                    <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                  </CollapsibleTrigger>
                </SidebarGroupLabel>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {workItems.map((item) => (
                        <SidebarMenuItem key={item.title} className={cn("relative transition-opacity duration-200", isTourDimmed(item.url) && "opacity-30 pointer-events-none")} {...(isTourPrimary(item.url) ? { "data-tour-active": "true" } : {})}>
                          <SidebarMenuButton
                            asChild
                            isActive={isRouteActive(item.url) || isTourHighlighted(item.url)}
                            data-testid={`link-${item.title.toLowerCase()}`}
                          >
                            <Link href={item.url} onClick={closeMobileDrawer}>
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>

            {/* Section 2: CUSTOMERS */}
            <Collapsible open={customersOpen} onOpenChange={sectionOnChange("CUSTOMERS", setCustomersOpen)} className="group/collapsible">
              <SidebarGroup>
                <SidebarGroupLabel asChild>
                  <CollapsibleTrigger className={cn("flex w-full items-center justify-between hover-elevate transition-opacity duration-200", isSectionDimmed("CUSTOMERS") && "opacity-30 pointer-events-none")}>
                    <span className="font-semibold text-sm">CUSTOMERS</span>
                    <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                  </CollapsibleTrigger>
                </SidebarGroupLabel>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {customerItems.map((item) => (
                        <SidebarMenuItem key={item.title} className={cn("relative transition-opacity duration-200", isTourDimmed(item.url) && "opacity-30 pointer-events-none")} {...(isTourPrimary(item.url) ? { "data-tour-active": "true" } : {})}>
                          <SidebarMenuButton
                            asChild
                            isActive={isRouteActive(item.url) || isTourHighlighted(item.url)}
                            data-testid={`link-${item.title.toLowerCase()}`}
                          >
                            <Link href={item.url} onClick={closeMobileDrawer}>
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                              {item.title === "Inbox" && inboxUnreadCount > 0 && (
                                <Badge variant="default" className="ml-auto bg-blue-500 text-white">
                                  {inboxUnreadCount}
                                </Badge>
                              )}
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>

            {/* Section 3: STAFF */}
            <Collapsible open={staffOpen} onOpenChange={sectionOnChange("STAFF", setStaffOpen)} className="group/collapsible">
              <SidebarGroup>
                <SidebarGroupLabel asChild>
                  <CollapsibleTrigger className={cn("flex w-full items-center justify-between hover-elevate transition-opacity duration-200", isSectionDimmed("STAFF") && "opacity-30 pointer-events-none")}>
                    <span className="font-semibold text-sm">STAFF</span>
                    <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                  </CollapsibleTrigger>
                </SidebarGroupLabel>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {staffManagementItems.filter(item => !item.adminOnly || isAdmin).map((item) => (
                        <SidebarMenuItem key={item.title} className={cn("relative transition-opacity duration-200", isTourDimmed(item.url) && "opacity-30 pointer-events-none")} {...(isTourPrimary(item.url) ? { "data-tour-active": "true" } : {})}>
                          <SidebarMenuButton
                            asChild
                            isActive={isRouteActive(item.url) || isTourHighlighted(item.url)}
                            data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            <Link href={item.url}>
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>

            {/* Section 4: OPERATIONS */}
            <Collapsible open={operationsOpen} onOpenChange={sectionOnChange("OPERATIONS", setOperationsOpen)} className="group/collapsible">
              <SidebarGroup>
                <SidebarGroupLabel asChild>
                  <CollapsibleTrigger className={cn("flex w-full items-center justify-between hover-elevate transition-opacity duration-200", isSectionDimmed("OPERATIONS") && "opacity-30 pointer-events-none")}>
                    <span className="font-semibold text-sm">OPERATIONS</span>
                    <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                  </CollapsibleTrigger>
                </SidebarGroupLabel>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {operationsItems.filter(item => !item.adminOnly || isAdmin).map((item) => (
                        <SidebarMenuItem key={item.title} className={cn("relative transition-opacity duration-200", isTourDimmed(item.url) && "opacity-30 pointer-events-none")} {...(isTourPrimary(item.url) ? { "data-tour-active": "true" } : {})}>
                          <SidebarMenuButton
                            asChild
                            isActive={isRouteActive(item.url) || isTourHighlighted(item.url)}
                            data-testid={`link-${item.title.toLowerCase()}`}
                          >
                            <Link href={item.url} onClick={closeMobileDrawer}>
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>

            {/* Section 4: SYSTEM */}
            <Collapsible open={systemOpen} onOpenChange={sectionOnChange("SYSTEM", setSystemOpen)} className="group/collapsible">
              <SidebarGroup>
                <SidebarGroupLabel asChild>
                  <CollapsibleTrigger className={cn("flex w-full items-center justify-between hover-elevate transition-opacity duration-200", isSectionDimmed("SYSTEM") && "opacity-30 pointer-events-none")}>
                    <span className="font-semibold text-sm">SYSTEM</span>
                    <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                  </CollapsibleTrigger>
                </SidebarGroupLabel>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {systemItems.filter(item => !item.adminOnly || isAdmin).map((item) => (
                        <SidebarMenuItem key={item.title} className={cn("relative transition-opacity duration-200", isTourDimmed(item.url) && "opacity-30 pointer-events-none")} {...(isTourPrimary(item.url) ? { "data-tour-active": "true" } : {})}>
                          <SidebarMenuButton
                            asChild
                            isActive={isRouteActive(item.url) || isTourHighlighted(item.url)}
                            data-testid={`link-${item.title.toLowerCase()}`}
                          >
                            <Link href={item.url} onClick={closeMobileDrawer}>
                              <item.icon className="h-4 w-4" />
                              <span>{item.title}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                      {/* Setup Guide — always visible for admin/owner */}
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          asChild
                          isActive={isRouteActive("/setup-guide")}
                          data-testid="link-setup-guide"
                        >
                          <Link href="/setup-guide">
                            <Rocket className="h-4 w-4" />
                            <span>Setup Guide</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>
          </>
        ) : (
          /* Staff View: Show Staff Portal only */
          <Collapsible open={staffPortalOpen} onOpenChange={sectionOnChange("MY WORKSPACE", setStaffPortalOpen)} className="group/collapsible">
            <SidebarGroup>
              <SidebarGroupLabel asChild>
                <CollapsibleTrigger className={cn("flex w-full items-center justify-between hover-elevate transition-opacity duration-200", isSectionDimmed("MY WORKSPACE") && "opacity-30 pointer-events-none")}>
                  <span className="font-semibold text-sm">MY WORKSPACE</span>
                  <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                </CollapsibleTrigger>
              </SidebarGroupLabel>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {staffItems.map((item) => (
                      <SidebarMenuItem key={item.title} className={cn("relative transition-opacity duration-200", isTourDimmed(item.url) && "opacity-30 pointer-events-none")} {...(isTourPrimary(item.url) ? { "data-tour-active": "true" } : {})}>
                        <SidebarMenuButton asChild isActive={isRouteActive(item.url) || isTourHighlighted(item.url)} data-testid={`link-${item.title.toLowerCase().replace(/\s+/g, "-")}`}>
                          <Link href={item.url} onClick={closeMobileDrawer}>
                            <item.icon className="h-4 w-4" />
                            <span>{item.title}</span>
                            {item.title === "Team Chat" && teamChatUnreadData && teamChatUnreadData.count > 0 && (
                              <Badge variant="destructive" className="ml-auto" data-testid="badge-team-chat-unread">
                                {teamChatUnreadData.count}
                              </Badge>
                            )}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                    {/* Setup Guide — always visible for staff */}
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={isRouteActive("/setup-guide")}
                        data-testid="link-setup-guide-staff"
                      >
                        <Link href="/setup-guide">
                          <Rocket className="h-4 w-4" />
                          <span>Setup Guide</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        )}
      </SidebarContent>
      <SidebarFooter className="p-4 space-y-3">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleLogout}
          className="w-full"
          data-testid="button-logout"
        >
          <LogOut className="h-4 w-4" />
          <span>Sign Out</span>
        </Button>
        <div className="text-xs text-muted-foreground space-y-0.5">
          <div>© 2026 All Matters Painting</div>
          <div className="text-[11px]">Quality Meets Care</div>
        </div>
      </SidebarFooter>

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent data-testid="dialog-logout-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Sign Out</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to sign out? You'll need to log in again to access your account.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              data-testid="button-cancel-logout"
              onClick={() => setShowLogoutDialog(false)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmLogout}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-logout"
            >
              Sign Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sidebar>
  );
}
